module agi.expressions.card.utils{
    /**
     * Interface used specifically for the SplitSpriteContainer options
     */
    interface SSCConfig{
        startIndex? : number;
        spriteCount? : number;
        separator?: string;
        ext?: string;
    }

    /**
     * Container class meant specifically to hold a sprite which has been split up into multiple pieces.
     * Note that in actuality, this is a container which has a container of split sprites. 
     * This is to facilitate situations where you want to add more children to this container which would not normally work because it uses cacheAsBitmap.
     * @borrows Brian Emling's code.
     * @author Dan Singer
     */
    export class SplitSpriteContainer extends PIXI.Container{

        protected spriteQuanity : number;
        protected splitSprites: PIXI.Container;
        /**
         * Construct a new SplitSpriteContainer.
         * @param baseTextureCacheId This is as it appears in the texture cache, w/o the extensions and separator. For example, web/images/cat
         * @param config see the SSCConfig interface. 
         * @description spriteCount value of -1 indicates that we should read the sprites in auto mode. 
         * This means that we'll keep reading in images from the texture cache until we can't find anymore.
         * This also means you don't need to specify how many sprites you've split the image into!
         */
        constructor(baseTextureCacheId:string, {startIndex=1, spriteCount=-1, separator="_", ext=".png"} : SSCConfig = {}){
            super();
            this.splitSprites = new PIXI.Container();
            if (spriteCount == 0 || spriteCount < -1){
                throw `Invalid spriteCount value, ${spriteCount}`;
            }

            let autoParse = () => {
                let curIndex = startIndex;
                while (1){
                    let id = baseTextureCacheId + separator + curIndex + ext;
                    if (!(id in PIXI.utils.TextureCache)){
                        break;
                    }
                    else{
                        this.splitSprites.addChild(PIXI.Sprite.fromFrame(id));
                    }
                    ++curIndex;
                }
                if (startIndex == curIndex)
                    throw "Unable to find any sprites in the Texture Cache to add to this container!";
                this.spriteQuanity = curIndex - startIndex;
            }

            let manualParse = () => {
                for(let i=startIndex; i < startIndex + spriteCount; i++){
                    let id = baseTextureCacheId + separator + i + ext;
                    if (!(id in PIXI.utils.TextureCache))
                        throw `Unable to find ${id} in the Texture Cache!`;
                    this.splitSprites.addChild(PIXI.Sprite.fromFrame(id));
                }
                this.spriteQuanity = spriteCount;
            }

            if (spriteCount == -1){
                autoParse();
            }
            else{
                manualParse();
            }
            this.splitSprites.cacheAsBitmap=true; 
            this.addChild(this.splitSprites);
        }

        /**
         * Return the number of sprites in this container
         */
        public getSpriteQuantity() : number {
            return this.spriteQuanity;
        }
    }


    /**
     * Factory function which will make a SSC or Sprite, depending on what's in the TextureCache
     * @param baseTextureCacheId excluding separator and ext 
     */
    export function makeGeneralizedSprite(baseTextureCacheId:string, {startIndex=1, spriteCount=-1, separator="_", ext=".png"} : SSCConfig = {}) : PIXI.Container{
        let curIndex = startIndex;
        let id = baseTextureCacheId + separator + curIndex + ext;
        if (id in PIXI.utils.TextureCache){
            return new SplitSpriteContainer(baseTextureCacheId, {startIndex, spriteCount, separator, ext});
        }
        else{
            let newId = baseTextureCacheId + ext;
            return PIXI.Sprite.fromFrame(newId);
        }
    }
}